// Транспорт и хуки раздела «Инициативы инженеров». ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/p73.ts и не api/p73g.ts. В первом — инженерный блок портала 73
// (работы по судам), он открыт всем ролям CRM. Во втором — баллы и выплаты. Здесь третье:
// вознаграждение за инициативу, 10 % чистой прибыли сделки, поимённо. Разряд данных тот же,
// что у кассы и ФОТ, а дверь — одна учётка владельца (rights.владелец, раздел
// 'initiatives'). Сложить файлы в один значило бы однажды переиспользовать хук не там.
//
// Подать заявку, вписать оценку и назначить вознаграждение отсюда нельзя и не будет можно:
// подача — запись в портал 73, оценка — запись с брифинга, сумма — правка пяти полей
// сделки в Bitrix, где на полях висят бизнес-процессы.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/p73i'

/**
 * Тот же разбор ответа, что в api/client.ts, api/p73.ts, api/p73g.ts.
 *
 * 400 от ядра здесь почти всегда одно и то же: «назовите человека либо запросите свод по
 * всем явно, all=1». Это ОТКАЗ, а не «инициатив нет», и превращать его в пустую таблицу
 * нельзя — пустая таблица про деньги читается как «человеку ничего не причитается».
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    if (res.status === 403) {
      // BFF отказал по учётке, а не по роли. Человеку надо сказать это словами: иначе
      // «forbidden» читается как поломка, и он идёт чинить то, что работает как задумано.
      throw new Error('раздел открыт одной учётке владельца — здесь вознаграждение людей '
                      + 'поимённо, тот же разряд данных, что касса и ФОТ')
    }
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. p73i_api.py) ───────────

/** Общая обёртка списка ядра + пометка «здесь деньги нескольких людей». */
export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  restricted?: string
  restricted_why?: string
}

/** Инициатор: id сотрудника Bitrix и его имя. Имени может не быть — человек уволен. */
export interface Инициатор {
  id: number
  name: string | null
  active: boolean | null
}

/** Строка реестра. Денежные поля приходят ТОЛЬКО при money=1 (умолчание ядра). */
export interface ИнициативаСтрока {
  deal_bitrix_id: number
  title: string | null
  category_id: number | null
  stage_id: string | null
  closed: boolean | null
  /** Стадия C4:9 или C4:WON — правило портала, а не наше. */
  counted: boolean
  init_date: string | null
  company_id: number | null
  vessel_contact_id: number | null
  assigned_by_id: number | null
  eng_people: Инициатор[]
  rem_people: Инициатор[]
  /** Диагнозы оформления: sum_text, sum_empty, no_who, no_date, deal_lost. */
  issues: string[]
  /** Сколько заявок инженеров по этой же сделке. Склейка по номеру, а не связь. */
  claims: number
  first_seen_at: string | null
  synced_at: string | null
  /** Дальше — только при money=1. Без него этих полей в ответе НЕТ вовсе. */
  eng_sum?: number | null
  eng_sum_raw?: string | null
  rem_sum?: number | null
  rem_sum_raw?: string | null
  opportunity?: number | null
  currency?: string | null
  /** Доля одного соинициатора: сумма делится поровну (правило портала). */
  eng_share?: number | null
}

export interface РеестрИтоги {
  deals: number
  counted_deals: number
  eng_sum: number
  eng_sum_counted: number
  rem_sum: number
  /** Сколько сумм разобрать не удалось: в поле текст вместо числа. */
  eng_sum_unparsed: number
  with_issues: number
}

export interface ЧеловекСтрока {
  bx_id: number
  name: string | null
  active: boolean | null
  /** Учётка портала 73 у того же человека. NULL — в портале его нет. */
  p73_d1_id: number | null
  initiatives: number
  as_engineer: number
  as_repairman: number
  counted_deals: number
  total_rub: number
  counted_rub: number
  pending_rub: number
  /** Сколько его инициатив с непонятной суммой. */
  unparsed: number
  first_date: string | null
  last_date: string | null
}

export interface ЛюдиИтоги {
  people: number
  total_rub: number
  counted_rub: number
  initiatives: number
}

export interface ЗаявкаСтрока {
  d1_id: number
  user_d1_id: number | null
  deal_d1_id: number | null
  deal_bitrix_id: number | null
  deal_title: string | null
  claim_text: string | null
  /** Оценка СТОИМОСТИ РАБОТЫ для сметы, а НЕ вознаграждение инженеру. */
  estimate_rub: number | null
  status: string | null
  estimated_by: string | null
  estimated_at: string | null
  processed_at: string | null
  created_at: string | null
  engineer: string | null
  engineer_bx: number | null
  processed_by: string | null
  /** Оформлена ли инициатива по этой сделке. Совпадение по номеру, а не связь. */
  in_registry: boolean | null
  /** Название сделки в зеркале сейчас. NULL — такой сделки в ядре нет. */
  deal_now: string | null
}

export interface ЗаявкиИтоги {
  claims: number
  need_estimate: number
  new: number
  processed: number
  people: number
  estimate_rub: number
}

export interface ДырыИтоги {
  deals: number
  sum_text: number
  sum_empty: number
  no_who: number
  no_date: number
  deal_lost: number
  eng_sum: number
}

export interface СвежестьP73I {
  entity: string
  last_sync_at: string | null
  last_full: string | null
  last_count: number | null
  age_min: number | null
}

export interface СчётчикиP73I {
  deals: number
  with_date: number
  counted_deals: number
  with_eng_sum_raw: number
  with_eng_sum: number
  eng_sum_unparsed: number
  with_rem_sum: number
  eng_sum_total: number
  eng_sum_counted: number
  people: number
  date_from: string | null
  date_to: string | null
  gone: number
  with_issues: number
  claims: number
  claim_people: number
  claims_open: number
  claims_estimated: number
}

export interface ПериодСтрока {
  period: string
  deals: number
  counted_deals: number
  eng_sum: number
}

export interface СтадияСтрока {
  stage_id: string
  deals: number
  counted: boolean
}

export interface ЧеловекСправочник {
  bx_id: number
  name: string | null
  active: boolean | null
  initiatives: number
}

export interface СправочникиP73I {
  freshness: СвежестьP73I[]
  counts: СчётчикиP73I
  periods: ПериодСтрока[]
  stages: СтадияСтрока[]
  claim_statuses: { status: string; claims: number }[]
  people: ЧеловекСправочник[]
  /** Оговорки о данных — со ВСТАВЛЕННЫМИ ядром числами. Экран печатает их как есть. */
  caveats: string[]
  issue_names: Record<string, string>
  status_names: Record<string, string>
  stage_names: Record<string, string>
  fields: Record<string, string>
  source: string
}

export interface ПроверкаСтрока {
  check: string
  rows: number
  why: string
}

export type Сходимость = Список<ПроверкаСтрока>

export interface ФильтрыP73I {
  /** Индекс нужен, чтобы фильтры уходили в строкаЗапроса() как обычный набор пар. */
  [ключ: string]: unknown
  all?: boolean
  engineer_bx?: number | string
  money?: boolean
  month?: string
  from?: string
  to?: string
  stage?: string
  counted?: boolean
  closed?: boolean
  deal?: number | string
  kind?: string
  status?: string
  sort?: string
  q?: string
  start?: number
  limit?: number
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export function useP73IRefs() {
  return useQuery<СправочникиP73I>({
    queryKey: ['p73i', 'refs'],
    queryFn: () => запрос<СправочникиP73I>('/refs'),
    ...ОБЩЕЕ,
  })
}

export function useРеестрИнициатив(ф: ФильтрыP73I) {
  return useQuery<Список<ИнициативаСтрока> & { totals?: РеестрИтоги; money: boolean }>({
    queryKey: ['p73i', 'list', ф],
    queryFn: () => запрос('/list' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useСводПоЛюдям(ф: ФильтрыP73I) {
  return useQuery<Список<ЧеловекСтрока> & { totals: ЛюдиИтоги }>({
    queryKey: ['p73i', 'people', ф],
    queryFn: () => запрос('/people' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useЗаявкиИнициатив(ф: ФильтрыP73I) {
  return useQuery<Список<ЗаявкаСтрока> & { totals: ЗаявкиИтоги }>({
    queryKey: ['p73i', 'claims', ф],
    queryFn: () => запрос('/claims' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useДыры(ф: ФильтрыP73I) {
  return useQuery<Список<ИнициативаСтрока> & { totals: ДырыИтоги; kind: string | null }>({
    queryKey: ['p73i', 'holes', ф],
    queryFn: () => запрос('/holes' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

/**
 * Сходимость. Адресата не требует вовсе (там нет ни одной суммы по людям), но пусть
 * вызов будет отдельным хуком: экран показывает её последней вкладкой.
 */
export function useСходимостьИнициатив() {
  return useQuery<Сходимость>({
    queryKey: ['p73i', 'checks'],
    queryFn: () => запрос<Сходимость>('/checks'),
    ...ОБЩЕЕ,
  })
}
